import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Check, AlertTriangle, Info, RefreshCw } from 'lucide-react';

/**
 * Consolidated-bill Validation tab. Extracted verbatim from ConsolidatedViewer
 * to keep that page maintainable. All validation math is computed in the parent
 * (it's shared with the header badge, recover logic and other tabs) and passed
 * in as props — this component is pure presentation.
 */
export function ValidationTab({
  mandatoryFieldsValidation,
  consolidatedValidation,
  consValidation,
  childReconciliation,
  summaryMatrix,
  meterCompleteness,
  accordionValue,
  onAccordionChange,
  onRevalidate,
  revalidating,
}) {
  // Accounts with NO line-item detail captured are "gaps" — the backend approval
  // gate treats these as unvalidated, so the banner must too (previously the
  // green "Validation Passed" ignored them and over-claimed).
  const gapAccounts = childReconciliation.rows.filter((r) => !r.hasLines).length;
  const fullyValid = consValidation.totalDueMatches
    && childReconciliation.bad === 0
    && gapAccounts === 0
    && mandatoryFieldsValidation.allPresent;
  return (
    <>
      {/* Mandatory Fields Banner */}
      {!mandatoryFieldsValidation.allPresent && (
        <div className="mb-4 p-4 rounded-none border-2 bg-red-50 border-red-500" data-testid="cons-validation-mandatory-warning">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-6 w-6 text-red-600 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-bold text-lg text-red-800">
                Missing Mandatory Statement Fields ({mandatoryFieldsValidation.missing.length} of {mandatoryFieldsValidation.total})
              </h3>
              <p className="text-sm text-red-700 mb-2">
                The following statement-level fields did not parse and must be filled in:
              </p>
              <div className="flex flex-wrap gap-2">
                {mandatoryFieldsValidation.missing.map((f) => (
                  <Badge key={f.key} variant="destructive" className="rounded-none" data-testid={`cons-validation-missing-${f.key}`}>
                    {f.label}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Truncated document banner */}
      {consolidatedValidation?.document_truncated && (
        <div className="mb-4 p-4 rounded-none border-2 bg-red-50 border-red-500" data-testid="cons-validation-truncated">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-6 w-6 text-red-600 mt-0.5" />
            <div>
              <h3 className="font-bold text-lg text-red-800">Incomplete PDF — Pages Missing</h3>
              <p className="text-sm text-red-700">
                The bill prints "Page 1 of {consolidatedValidation.document_truncated.printed_pages}" but this PDF contains
                only {consolidatedValidation.document_truncated.actual_pages} page{consolidatedValidation.document_truncated.actual_pages > 1 ? 's' : ''}.
                Per-account detail pages are missing — amounts for those accounts come from the statement summary table only.
                Re-upload the full document for complete extraction.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Statement extraction failure banner */}
      {(consValidation.statementEmpty || (consolidatedValidation && consolidatedValidation.statement_extraction_failed)) && (
        <div className="mb-4 p-4 rounded-none border-2 bg-red-50 border-red-500" data-testid="cons-validation-statement-failed">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-6 w-6 text-red-600 mt-0.5" />
            <div>
              <h3 className="font-bold text-lg text-red-800">Statement Totals Did Not Parse</h3>
              <p className="text-sm text-red-700">
                The statement-level totals (Total Charges / Total Amount Due) came back empty from extraction,
                so parent-level validations cannot run. Fill them in on the Statement tab or reprocess the document.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Overall Status */}
      <div className={`mb-4 p-4 rounded-none border-2 ${fullyValid ? 'bg-green-50 border-green-500' : 'bg-yellow-50 border-yellow-500'}`} data-testid="cons-validation-status">
        <div className="flex items-start gap-3">
          {fullyValid ? (
            <Check className="h-6 w-6 text-green-600 mt-0.5" />
          ) : (
            <AlertTriangle className="h-6 w-6 text-yellow-600 mt-0.5" />
          )}
          <div className="flex-1">
            <h3 className={`font-bold text-lg ${fullyValid ? 'text-green-800' : 'text-yellow-800'}`}>
              {fullyValid ? 'Validation Passed' : 'Validation Warnings'}
            </h3>
            <p className="text-sm mt-1 text-muted-foreground" data-testid="cons-validation-summary-line">
              {fullyValid
                ? 'The statement reconciles, every account has line-item detail, and all mandatory fields are present. Expand the checks below for detail.'
                : 'One or more checks need attention — the affected checks are expanded below.'}
            </p>
            {childReconciliation.bad > 0 && (
              <p className="text-sm text-yellow-800 mt-1" data-testid="cons-validation-badchild-line">
                {childReconciliation.bad} account{childReconciliation.bad > 1 ? 's' : ''} where line items do not reconcile to the account's new charges — see table below.
              </p>
            )}
            {gapAccounts > 0 && (
              <p className="text-sm text-yellow-800 mt-1" data-testid="cons-validation-gap-line">
                {gapAccounts} of {childReconciliation.rows.length} account{gapAccounts > 1 ? 's have' : ' has'} no line-item detail captured yet — the statement total still reconciles, but this bill is not fully validated. Run <strong>Auto recover</strong> to pull those line items (no re-parse, no AI credits).
              </p>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onRevalidate}
            disabled={revalidating}
            className="rounded-none shrink-0 bg-white"
            data-testid="cons-validation-revalidate-btn"
            title="Re-run all validation checks from the already-parsed data — uses no AI credits"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${revalidating ? 'animate-spin' : ''}`} />
            {revalidating ? 'Revalidating…' : 'Revalidate'}
          </Button>
        </div>
      </div>

      <Accordion type="multiple" value={accordionValue} onValueChange={onAccordionChange} className="border border-border divide-y divide-border mb-4" data-testid="cons-validation-accordion">
        {/* Total check */}
        <AccordionItem value="total" className="border-b-0 px-4" data-testid="cons-validation-acc-total">
          <AccordionTrigger data-testid="cons-validation-acc-total-trigger">
            <span className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide">
              Total Amount Due Check
              {consValidation.totalDueMatches
                ? <Check className="h-4 w-4 text-green-600" />
                : <AlertTriangle className="h-4 w-4 text-red-600" />}
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm font-mono">
              <div>Balance Forward{consValidation.bbfIsCalculated ? <span className="italic text-muted-foreground"> (calculated)</span> : ''}</div><div className="text-right" data-testid="cons-validation-bbf">${consValidation.balanceForward.toFixed(2)}</div>
              <div>+ Current Charges (New Charges)</div><div className="text-right">${consValidation.totalCharges.toFixed(2)}</div>
              <div>+ Late Fee</div><div className="text-right">${consValidation.lateFee.toFixed(2)}</div>
              <div>{consValidation.adjustments < 0 ? '−' : '+'} Adjustments</div><div className="text-right" data-testid="cons-validation-adjustments">${Math.abs(consValidation.adjustments).toFixed(2)}</div>
              <div>− Credits</div><div className="text-right">${consValidation.credits.toFixed(2)}</div>
              <div className="border-t pt-2 font-bold">= Calculated Total</div>
              <div className="text-right border-t pt-2 font-bold" data-testid="cons-validation-calculated-total">${consValidation.totalDueFormula.toFixed(2)}</div>
              <div className="font-bold">Stated Total Due</div>
              <div className="text-right font-bold" data-testid="cons-validation-stated-total">${consValidation.totalAmountDue.toFixed(2)}</div>
              <div className={`font-bold ${consValidation.totalDueMatches ? 'text-green-700' : 'text-red-700'}`}>Difference{consValidation.totalDueMatches ? '' : ' — does not reconcile'}</div>
              <div className={`text-right font-bold ${consValidation.totalDueMatches ? 'text-green-700' : 'text-red-700'}`} data-testid="cons-validation-difference">
                ${Math.abs(consValidation.totalDueFormula - consValidation.totalAmountDue).toFixed(2)}
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              Total Amount Due = Balance Forward + Current Charges + Late Fee + Adjustments − Credits. When the bill prints no Balance Forward line it is calculated as Previous Balance − Payments Received, and any residual is shown on the Adjustments line (never folded into Balance Forward). If the equation does not close, the check is flagged red.
            </p>
            {/* Secondary cross-check — informational only. Σ of each
                account's Total Amount Due rarely equals the bill's net
                Total when sub-accounts carry their own prior balance /
                payments, so a difference here is EXPECTED and must never
                contradict the reconciled header — show it muted, not red. */}
            {consValidation.childDueSum > 0 && (
              <div className="mt-3 pt-3 border-t text-xs" data-testid="cons-validation-childdue-check">
                {consValidation.childDueMatches ? (
                  <span className="text-muted-foreground inline-flex items-center gap-1 flex-wrap">
                    Cross-check — Σ accounts' Total Amount Due (${consValidation.childDueSum.toFixed(2)}) vs bill (${consValidation.totalAmountDue.toFixed(2)}):
                    <span className="text-green-700 font-medium inline-flex items-center gap-1"><Check className="h-3.5 w-3.5" />matches</span>
                  </span>
                ) : (
                  <span className="text-muted-foreground inline-flex items-start gap-1">
                    <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    <span data-testid="cons-validation-childdue-note">
                      Σ of each account's Total Amount Due is ${consValidation.childDueSum.toFixed(2)} vs the bill's ${consValidation.totalAmountDue.toFixed(2)} (differs by ${Math.abs(consValidation.childDueSum - consValidation.totalAmountDue).toFixed(2)}).
                      This is expected — sub-account totals include each account's own prior balance/payments, which net out at the parent level. The bill's Total Amount Due above is the authoritative figure.
                    </span>
                  </span>
                )}
              </div>
            )}
          </AccordionContent>
        </AccordionItem>

        {/* BBF check */}
        {(consValidation.bbfMatches !== null || consValidation.bbfIsCalculated) && (
        <AccordionItem value="bbf" className="border-b-0 px-4" data-testid="cons-validation-acc-bbf">
          <AccordionTrigger data-testid="cons-validation-acc-bbf-trigger">
            <span className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide">
              Balance Forward Check
              {consValidation.bbfMatches === false
                ? <AlertTriangle className="h-4 w-4 text-red-600" />
                : <Check className="h-4 w-4 text-green-600" />}
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm font-mono">
              <div>Previous Balance</div><div className="text-right">${consValidation.previousBalance.toFixed(2)}</div>
              <div>− Payments Received</div><div className="text-right">${consValidation.paymentsReceived.toFixed(2)}</div>
              <div className="border-t pt-2 font-bold">= Balance Forward{consValidation.bbfIsCalculated ? <span className="italic font-normal text-muted-foreground"> (calculated)</span> : ''}</div>
              <div className="text-right border-t pt-2 font-bold" data-testid="cons-validation-bbf-calculated">${consValidation.calculatedBBF.toFixed(2)}</div>
              {!consValidation.bbfIsCalculated && (
                <>
                  <div className="font-bold">Printed Balance Forward</div>
                  <div className="text-right font-bold">${consValidation.printedBBF.toFixed(2)}</div>
                </>
              )}
            </div>
            {consValidation.bbfIsCalculated && (
              <p className="text-xs text-muted-foreground mt-2" data-testid="cons-validation-bbf-calculated-note">
                This bill prints no separate Balance Forward line, so it is <strong>calculated</strong> as Previous Balance − Payments Received. Any prior adjustment or credit is shown on the Adjustments line above — it is never folded into the Balance Forward.
              </p>
            )}
            {consValidation.bbfMatches === false && (
              <p className="text-xs text-red-700 mt-2">
                Previous Balance − Payments Received (even allowing for credits/refunds) does not equal the printed Balance Forward — verify the Payments Received / Previous Balance values on the Statement tab.
              </p>
            )}
          </AccordionContent>
        </AccordionItem>
        )}

        {/* Summary ↔ Child Pages check — hidden entirely when no
            per-account summary table was captured (avoids the noisy
            empty "Not captured" item; per-account reconciliation below
            already covers this). */}
        {summaryMatrix.length > 0 && (
        <AccordionItem value="summary" className="border-b-0 px-4" data-testid="cons-validation-acc-summary">
          <AccordionTrigger data-testid="cons-validation-acc-summary-trigger">
            <span className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide">
              Statement Summary ↔ Child Pages
              {summaryMatrix.some((r) => r.status === 'mismatch' || r.status === 'missing_child')
                ? <AlertTriangle className="h-4 w-4 text-red-600" />
                : <Check className="h-4 w-4 text-green-600" />}
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <div data-testid="cons-validation-summary-matrix">
              <p className="text-xs text-muted-foreground mb-3">
                Each account row printed on the statement's summary table compared against the totals extracted from that account's detail pages.
              </p>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs uppercase text-muted-foreground">
                    <th className="py-1 pr-2">Account</th>
                    <th className="py-1 pr-2 text-right">Summary $</th>
                    <th className="py-1 pr-2 text-right">Credits</th>
                    <th className="py-1 pr-2 text-right">Adj</th>
                    <th className="py-1 pr-2 text-right">Child Pages $</th>
                    <th className="py-1 pr-2 text-right">Diff</th>
                    <th className="py-1">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {summaryMatrix.map((row, i) => (
                    <tr key={i} className="border-b border-border/50 font-mono" data-testid={`cons-summary-matrix-row-${i}`}>
                      <td className="py-1 pr-2">{row.account_no || '—'}{row.child_entries > 1 ? ` (×${row.child_entries})` : ''}</td>
                      <td className="py-1 pr-2 text-right">{row.summary_amount === null || row.summary_amount === undefined ? '—' : `$${Number(row.summary_amount).toFixed(2)}`}</td>
                      <td className="py-1 pr-2 text-right">{row.summary_credits ? `$${Number(row.summary_credits).toFixed(2)}` : '—'}</td>
                      <td className="py-1 pr-2 text-right">{row.summary_adjustments ? `$${Number(row.summary_adjustments).toFixed(2)}` : '—'}</td>
                      <td className="py-1 pr-2 text-right">{`$${Number(row.child_new_charges || 0).toFixed(2)}`}</td>
                      <td className={`py-1 pr-2 text-right ${row.status === 'matched' ? 'text-green-700' : row.status === 'detail_missing' ? 'text-amber-700' : 'text-red-700'}`}>
                        {row.status === 'matched' ? '$0.00' : (row.difference === null || row.difference === undefined ? '—' : `$${Number(row.difference).toFixed(2)}`)}
                      </td>
                      <td className="py-1">
                        {row.status === 'matched' && <Badge className="rounded-none bg-green-100 text-green-800 hover:bg-green-100">Matched</Badge>}
                        {row.status === 'mismatch' && <Badge variant="destructive" className="rounded-none">Mismatch</Badge>}
                        {row.status === 'missing_child' && <Badge variant="destructive" className="rounded-none">No child pages</Badge>}
                        {row.status === 'detail_missing' && <Badge className="rounded-none bg-amber-100 text-amber-800 hover:bg-amber-100">Detail pages missing</Badge>}
                        {row.status === 'missing_summary_row' && <Badge className="rounded-none bg-yellow-100 text-yellow-800 hover:bg-yellow-100">Not on summary</Badge>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </AccordionContent>
        </AccordionItem>
        )}

        {/* Line item check */}
        {childReconciliation.rows.length > 0 && (
        <AccordionItem value="lineitem" className="border-b-0 px-4" data-testid="cons-validation-acc-lineitem">
          <AccordionTrigger data-testid="cons-validation-acc-lineitem-trigger">
            <span className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide">
              Per-Account Line-Item Reconciliation
              {childReconciliation.bad > 0
                ? <AlertTriangle className="h-4 w-4 text-red-600" />
                : <Check className="h-4 w-4 text-green-600" />}
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <div data-testid="cons-validation-child-recon">
              <p className="text-xs text-muted-foreground mb-3">
                For each account: sum(usage line items) + sum(other charges) + late fee is reconciled against the account's <strong>Current / New Charges</strong> (like-for-like — NOT the Total Amount Due). The <strong>Adjustment</strong> column shows Total − New Charges so any adjustment is visible, not swallowed. Updates live as you edit.
              </p>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs uppercase text-muted-foreground">
                    <th className="py-1 pr-2">Account</th>
                    <th className="py-1 pr-2 text-right">New Charges</th>
                    <th className="py-1 pr-2 text-right">Line Items Σ</th>
                    <th className="py-1 pr-2 text-right">Diff</th>
                    <th className="py-1 pr-2 text-right">Adjustment</th>
                    <th className="py-1 pr-2 text-right">Total Due</th>
                    <th className="py-1">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {childReconciliation.rows.map((r, i) => (
                    <tr key={i} className="border-b border-border/50 font-mono" data-testid={`cons-child-recon-row-${i}`}>
                      <td className="py-1 pr-2">{r.account}{r.count > 1 ? ` (×${r.count})` : ''}</td>
                      <td className="py-1 pr-2 text-right">${r.newCharges.toFixed(2)}</td>
                      <td className="py-1 pr-2 text-right">${r.lineItemSum.toFixed(2)}</td>
                      <td className={`py-1 pr-2 text-right ${!r.hasLines ? 'text-muted-foreground' : (r.ok ? 'text-green-700' : 'text-red-700')}`}>${(Math.abs(r.diff) < 0.005 ? 0 : r.diff).toFixed(2)}</td>
                      <td className="py-1 pr-2 text-right text-muted-foreground" data-testid={`cons-child-recon-adj-${i}`}>{Math.abs(r.impliedAdj) < 0.005 ? '$0.00' : `${r.impliedAdj < 0 ? '−' : '+'}$${Math.abs(r.impliedAdj).toFixed(2)}`}</td>
                      <td className="py-1 pr-2 text-right">${r.totalDue.toFixed(2)}</td>
                      <td className="py-1">
                        {!r.hasLines
                          ? <Badge variant="outline" className="rounded-none">No line items</Badge>
                          : r.ok
                            ? <Badge className="rounded-none bg-green-100 text-green-800 hover:bg-green-100">Reconciled</Badge>
                            : <Badge variant="destructive" className="rounded-none">Off by ${Math.abs(r.diff).toFixed(2)}</Badge>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </AccordionContent>
        </AccordionItem>
        )}

        {/* Meter completeness — INFORMATIONAL (never blocks approval) */}
        {meterCompleteness.rows.length > 0 && (
        <AccordionItem value="meter" className="border-b-0 px-4" data-testid="cons-validation-acc-meter">
          <AccordionTrigger data-testid="cons-validation-acc-meter-trigger">
            <span className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide">
              Meter Completeness
              {meterCompleteness.ok
                ? <Check className="h-4 w-4 text-green-600" />
                : <AlertTriangle className="h-4 w-4 text-blue-600" />}
              <Badge variant="outline" className="rounded-none text-[10px]">Informational</Badge>
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <div data-testid="cons-validation-meter-check">
              <p className="text-xs text-muted-foreground mb-3">
                Expected meters per account = number of distinct commodities present (electric / gas / water / sewer …).
                A bill with N commodities should have at least N meters. This check is a completeness signal only and
                <strong> never blocks approval</strong>.
                {' '}Total extracted <span className="font-mono">{meterCompleteness.extractedTotal}</span> vs expected <span className="font-mono">{meterCompleteness.expectedTotal}</span>
                {meterCompleteness.flagged > 0
                  ? <span className="text-blue-700 font-medium"> — {meterCompleteness.flagged} account{meterCompleteness.flagged > 1 ? 's' : ''} may be missing a meter.</span>
                  : <span className="text-green-700 font-medium"> — all accounts have a meter for every commodity.</span>}
              </p>
              {meterCompleteness.flagged > 0 && (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-xs uppercase text-muted-foreground">
                      <th className="py-1 pr-2">Account</th>
                      <th className="py-1 pr-2">Commodities</th>
                      <th className="py-1 pr-2 text-right">Expected</th>
                      <th className="py-1 pr-2 text-right">Extracted</th>
                      <th className="py-1">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {meterCompleteness.rows.filter((r) => !r.ok).map((r, i) => (
                      <tr key={i} className="border-b border-border/50 font-mono" data-testid={`cons-meter-check-row-${i}`}>
                        <td className="py-1 pr-2">{r.account || '—'}</td>
                        <td className="py-1 pr-2 font-sans text-xs">{r.commodities.join(', ') || '—'}</td>
                        <td className="py-1 pr-2 text-right">{r.expected}</td>
                        <td className="py-1 pr-2 text-right">{r.extracted}</td>
                        <td className="py-1">
                          <Badge variant="outline" className="rounded-none text-blue-700 border-blue-300">
                            {r.extracted === 0 ? 'No meter read' : 'Fewer than commodities'}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>
        )}

        {/* Horizontal line-item checks (usage × rate = charges) */}
        {consolidatedValidation?.horizontal_line_checks?.checked > 0 && (
        <AccordionItem value="horizontal" className="border-b-0 px-4" data-testid="cons-validation-acc-horizontal">
          <AccordionTrigger data-testid="cons-validation-acc-horizontal-trigger">
            <span className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide">
              Horizontal Line Checks (Usage × Rate = Charge)
              {consolidatedValidation.horizontal_line_checks.failed === 0
                ? <Check className="h-4 w-4 text-green-600" />
                : <AlertTriangle className="h-4 w-4 text-red-600" />}
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <div data-testid="cons-validation-horizontal">
              <p className="text-xs text-muted-foreground mb-2">
                {consolidatedValidation.horizontal_line_checks.passed} of {consolidatedValidation.horizontal_line_checks.checked} usage lines reconcile horizontally (quantity × rate = line charge).
              </p>
              {(consolidatedValidation.horizontal_line_checks.failures || []).length > 0 && (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-xs uppercase text-muted-foreground">
                      <th className="py-1 pr-2">Account</th>
                      <th className="py-1 pr-2">Line</th>
                      <th className="py-1 pr-2 text-right">Usage</th>
                      <th className="py-1 pr-2 text-right">Rate</th>
                      <th className="py-1 pr-2 text-right">Charge</th>
                      <th className="py-1 pr-2 text-right">Expected</th>
                    </tr>
                  </thead>
                  <tbody>
                    {consolidatedValidation.horizontal_line_checks.failures.map((f, i) => (
                      <tr key={i} className="border-b border-border/50 font-mono text-xs" data-testid={`cons-horizontal-fail-${i}`}>
                        <td className="py-1 pr-2">{f.child_account_no || '—'}</td>
                        <td className="py-1 pr-2 font-sans">{f.line_item_description || '—'}</td>
                        <td className="py-1 pr-2 text-right">{f.usage}</td>
                        <td className="py-1 pr-2 text-right">{f.usage_rate}</td>
                        <td className="py-1 pr-2 text-right text-red-700">${Number(f.charges).toFixed(2)}</td>
                        <td className="py-1 pr-2 text-right">${Number(f.expected).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>
        )}
      </Accordion>

      {/* Duplicate account warning */}
      {childReconciliation.dups.length > 0 && (
        <div className="border-2 border-yellow-500 bg-yellow-50 p-4 mb-4" data-testid="cons-validation-duplicates">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
            <div>
              <h4 className="font-semibold text-sm text-yellow-800">Repeated Account Numbers</h4>
              <p className="text-xs text-yellow-800 mt-1">
                {childReconciliation.dups.map((d) => `${d.account} (×${d.count})`).join(', ')} — the same account appears in multiple child invoices.
                Verify these are distinct service points/periods and not a double-extraction.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ValidationTab;
